/**
 * Unified Invoices Page
 *
 * Single list of ALL invoices (no In-Store / Online tabs).
 * Each invoice can accept payments via Cash, Card, Tyro, or Stripe link.
 * Click opens full detail with payment history.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, saleRefundRows, loadSaleInvoiceExtras, money, formatApiError, fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Receipt,
  MagnifyingGlass,
  Download,
  PaperPlaneTilt,
  CopySimple,
  ArrowClockwise,
  Globe,
  CurrencyDollar,
  CreditCard,
  Plus,
  CheckCircle,
  Clock,
  X,
  Warning,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import {
  OI_STATUS_COLORS,
  OI_STATUS_LABELS,
  copyInvoiceLink,
  fmtInvoiceDate,
} from "../components/OnlineInvoiceDetailModal";
import UnifiedInvoiceModal, { AddPaymentModal } from "../components/UnifiedInvoiceModal";
import DateRangeFilter from "../components/DateRangeFilter";

const STATUS_TABS = ["all", "paid", "balance_due", "refunded", "voided"];
const STATUS_LABELS_MAP = {
  all: "All",
  paid: "Paid",
  balance_due: "Balance due",
  refunded: "Refunded",
  part_refunded: "Part refunded",
  voided: "Voided",
};

/** Compute overpaid/underpaid delta for a sale or online invoice row */
function paymentDelta(sale) {
  const total = parseFloat(sale.total || 0);
  const inStore = parseFloat(sale.in_store_paid || 0);
  const stripe = parseFloat(sale.stripe_paid_total || 0);
  const tendered = parseFloat(sale.amount_tendered || 0);
  const refunded = parseFloat(sale.refund_total || 0);
  const totalPaid = (inStore > 0 || stripe > 0) ? (inStore + stripe) : tendered;
  const netPaid = Math.round((totalPaid - refunded) * 100) / 100;
  const balanceDue = parseFloat(sale.balance_due || 0);

  // Overpaid: customer paid more than the invoice total
  if (netPaid > 0.01 && total > 0) {
    const diff = Math.round((netPaid - total) * 100) / 100;
    if (diff > 0.01) return { overpaid: true, amount: diff, netPaid };
  }

  // Underpaid: any outstanding balance (even if zero payments made)
  if (balanceDue > 0.01) {
    return { overpaid: false, amount: Math.round(balanceDue * 100) / 100, netPaid };
  }

  return null;
}

const SOURCE_TABS = []; // removed — unified view only

function getUnifiedStatus(inv) {
  const refunded = parseFloat(inv.refund_total || 0);
  if (refunded > 0.005) {
    if (refunded >= parseFloat(inv.total || 0) - 0.005) return "refunded";
    // Still owing money → keep it in Balance due so it's chased.
    if (!(inv.voided || inv.status === "voided") && parseFloat(inv.balance_due || 0) > 0.005) return "balance_due";
    return "part_refunded";
  }
  if (inv.voided || inv.status === "voided") return "voided";
  if (inv.status === "paid" || inv._oi_status === "paid") return "paid";
  // /sales/summary returns `balance_due` (same field POS sales use) — NOT
  // `amount_due` (that's the online_invoices detail doc's own field name).
  // Trusting `balance_due` here fixes Stripe-paid invoices showing as
  // "Balance due" in the list while their detail modal correctly says Paid.
  if (inv.balance_due != null) {
    return parseFloat(inv.balance_due) <= 0.005 ? "paid" : "balance_due";
  }
  const total = parseFloat(inv.total || 0);
  const tendered = parseFloat(inv.amount_tendered || inv.in_store_paid || 0);
  const due = inv.amount_due != null ? parseFloat(inv.amount_due) : total - tendered;
  if (due <= 0.005) return "paid";
  return "balance_due";
}

export default function Sales() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ mode: "all", from: null, to: null });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [sales, setSales] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [addPaymentInvoice, setAddPaymentInvoice] = useState(null);

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (dateRange.from && dateRange.to) {
        // Local calendar date (toISOString would shift to the UTC day, e.g. 31 Oct AEST → 30 Oct).
        const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        params.set("start", ymd(dateRange.from));
        // `to` is exclusive (next-day midnight), so go back 1 day for the API
        params.set("end", ymd(new Date(dateRange.to.getTime() - 1)));
      }
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/sales/summary?${params}`);
      setSales(data.items || []);
    } catch { setSales([]); }
    finally { setLoading(false); }
  }, [dateRange, q]);

  useEffect(() => { loadSales(); }, [loadSales]);

  // Filter by status
  const filtered = sales.filter((s) => {
    const uStatus = getUnifiedStatus(s);
    if (statusFilter === "refunded") return uStatus === "refunded" || uStatus === "part_refunded";
    if (statusFilter !== "all" && uStatus !== statusFilter) return false;
    return true;
  });

  // Counts for tabs
  const counts = { all: 0, paid: 0, balance_due: 0, refunded: 0, voided: 0 };
  sales.forEach((s) => {
    const st = getUnifiedStatus(s) === "part_refunded" ? "refunded" : getUnifiedStatus(s);
    counts.all++;
    if (counts[st] !== undefined) counts[st]++;
  });

  const filteredTotal = filtered.reduce((s, inv) => s + parseFloat(inv.total || 0), 0);

  const METHOD_LABELS = { cash: "Cash", tyro: "TYRO", card: "Card", card_manual: "Card", stripe: "Stripe", other: "Other" };

  const openDetail = async (sale) => {
    try {
      // If this is a standalone online invoice (merged from online_invoices collection)
      if (sale._is_online_invoice) {
        const { data: detail } = await api.get(`/online-invoices/${sale.id}/detail`);
        setSelectedInvoice(detail);
        return;
      }

      // Fetch full sale record with items + payments
      const { data: fullSale } = await api.get(`/sales/${sale.id}`);
      
      // Linked online invoice (richer data) + warranty, fetched together.
      const { linkedInvoice, warranty } = await loadSaleInvoiceExtras(fullSale);

      if (linkedInvoice) {
        // Use the rich online invoice detail but keep S-series number
        linkedInvoice.invoice_number = fullSale.sale_number || linkedInvoice.invoice_number;
        // This modal is displaying the online-invoice presentation of a sale.
        // Void/delete/payment actions must still target the authoritative sale
        // record, otherwise the online-invoice route returns a misleading 404.
        linkedInvoice.backing_sale_id = fullSale.id;
        setSelectedInvoice(linkedInvoice);
      } else {
        // Build detail from the sale record
        const payments = (fullSale.payments || []).map((p, i) => ({
          payment_id: p.id || null,
          payment_index: i,
          method: p.method || "cash",
          amount: p.amount || 0,
          paid_at: p.paid_at || p.at || fullSale.created_at,
          cashier: p.cashier || p.cashier_name || fullSale.cashier_name || "Staff",
          is_refund: p.is_refund || false,
          note: p.note || "",
          tyro_meta: p.tyro_meta || null,
          corrected_at: p.corrected_at || null,
          correction_reason: p.correction_reason || "",
          tyro_offline: !!p.tyro_offline,
          tyro_auth_code: p.tyro_auth_code || "",
          edited_at: p.edited_at || null,
          edit_reason: p.edit_reason || "",
          edited_by_name: p.edited_by_name || "",
        }));
        const totalPaid = payments.reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : p.amount), 0);
        const amountDue = Math.max(0, parseFloat(fullSale.total || 0) - totalPaid);

        setSelectedInvoice({
          id: fullSale.id,
          invoice_number: fullSale.sale_number,
          customer_id: fullSale.customer_id || null,
          customer_name: fullSale.customer_name || "Walk-in",
          customer_phone: fullSale.customer_phone || "",
          customer_email: fullSale.customer_email || "",
          total: fullSale.total,
          discount: fullSale.discount || 0,
          store_credit_redeemed: fullSale.store_credit_redeemed || 0,
          amount_due: amountDue,
          in_store_paid: totalPaid,
          status: fullSale.voided ? "voided" : amountDue <= 0.005 ? "paid" : "draft",
          items: (fullSale.items || []).map(it => ({
            description: it.name || it.description || "Item",
            quantity: it.quantity || 1,
            unit_price: it.unit_price || it.price || 0,
            device_label: it.device_label || null,
            kind: it.kind || "product",
            repair_ticket_id: it.repair_ticket_id || null,
          })),
          payment_history: [...payments, ...saleRefundRows(fullSale)],
          devices: warranty.devices,
          accessories_warranty: warranty.accessories_warranty,
          item_warranties: warranty.item_warranties,
          created_at: fullSale.created_at,
          linked_ticket_id: (fullSale.items || []).find(it => it.repair_ticket_id)?.repair_ticket_id || null,
          _is_sale: true,
        });
      }
    } catch (err) {
      toast.error("Failed to load invoice detail");
    }
  };

  const paidTotal = sales.filter(s => getUnifiedStatus(s) === "paid").reduce((s, inv) => s + parseFloat(inv.total || 0), 0);
  const balanceDueTotal = sales.filter(s => getUnifiedStatus(s) === "balance_due").reduce((s, inv) => s + parseFloat(inv.balance_due || inv.amount_due || 0), 0);

  return (
    <Layout>
      {/* ── Page Header ── */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] font-bold mb-0.5">Invoices</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#0F172A] tracking-tight">Invoice History</h1>
          </div>
          {/* KPI cards */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
              <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center">
                <Receipt size={15} weight="bold" className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#94A3B8] font-bold leading-none mb-0.5">Total</div>
                <div className="font-mono text-lg font-extrabold text-[#0F172A] tabular-nums leading-none">${money(filteredTotal)}</div>
              </div>
              <div className="ml-1 text-[10px] text-[#94A3B8] font-semibold tabular-nums">{filtered.length}</div>
            </div>
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-emerald-50/60 border border-emerald-200/60">
              <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                <CheckCircle size={15} weight="bold" className="text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-emerald-600/70 font-bold leading-none mb-0.5">Paid</div>
                <div className="font-mono text-lg font-extrabold text-emerald-700 tabular-nums leading-none">${money(paidTotal)}</div>
              </div>
              <div className="ml-1 text-[10px] text-emerald-500 font-semibold tabular-nums">{counts.paid}</div>
            </div>
            {counts.balance_due > 0 && (
              <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-amber-50/60 border border-amber-200/60">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                  <Clock size={15} weight="bold" className="text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-amber-600/70 font-bold leading-none mb-0.5">Outstanding</div>
                  <div className="font-mono text-lg font-extrabold text-amber-700 tabular-nums leading-none">${money(balanceDueTotal)}</div>
                </div>
                <div className="ml-1 text-[10px] text-amber-500 font-semibold tabular-nums">{counts.balance_due}</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Search + Date Filter toolbar ── */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-xl">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by invoice #, customer name, phone or cashier..."
              className="w-full h-11 pl-11 pr-10 rounded-xl border border-[#E2E8F0] bg-white text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#1E5FEB] focus:ring-2 focus:ring-[#1E5FEB]/10 focus:outline-none transition-all"
              data-testid="invoices-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#64748B] hover:bg-[#E2E8F0] hover:text-[#0F172A] transition-colors"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
          {/* Date filter */}
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      <section className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        {/* ── Status pills ── */}
        <div className="px-5 py-3 border-b border-[#F1F5F9] bg-[#FAFBFC]">
          <div className="flex items-center gap-1.5">
            {STATUS_TABS.map((tab) => {
              const active = statusFilter === tab;
              const styles = {
                all: { dot: "", activeBg: "bg-[#0F172A]", activeText: "text-white" },
                paid: { dot: "bg-emerald-500", activeBg: "bg-emerald-600", activeText: "text-white" },
                balance_due: { dot: "bg-amber-500", activeBg: "bg-amber-500", activeText: "text-white" },
                refunded: { dot: "bg-rose-500", activeBg: "bg-rose-600", activeText: "text-white" },
                voided: { dot: "bg-[#94A3B8]", activeBg: "bg-[#64748B]", activeText: "text-white" },
              }[tab];
              return (
                <button
                  key={tab}
                  onClick={() => setStatusFilter(tab)}
                  className={`h-9 px-4 rounded-lg text-[12px] font-bold transition-all duration-200 whitespace-nowrap flex items-center gap-2 ${
                    active
                      ? `${styles.activeBg} ${styles.activeText} shadow-sm`
                      : "text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
                  }`}
                  data-testid={`filter-${tab}`}
                >
                  {styles.dot && <span className={`w-2 h-2 rounded-full ${active ? "bg-white/50" : styles.dot}`} />}
                  {STATUS_LABELS_MAP[tab]}
                  <span className={`min-w-[24px] h-5 px-1.5 rounded-md text-[10px] font-bold tabular-nums inline-flex items-center justify-center ${
                    active ? "bg-white/20 text-white/80" : "bg-[#F1F5F9] text-[#94A3B8]"
                  }`}>
                    {counts[tab] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-[110px_1.5fr_160px_110px_120px_60px] gap-3 px-5 py-2.5 border-b border-[#F1F5F9] text-[10px] tracking-[0.14em] uppercase text-[#94A3B8] font-bold">
          <div>Invoice</div>
          <div>Customer</div>
          <div>Date</div>
          <div className="text-center">Status</div>
          <div className="text-right">Total</div>
          <div className="text-center">PDF</div>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#94A3B8] text-[13px]">
            <ArrowClockwise size={16} className="animate-spin mr-2" /> Loading invoices...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#94A3B8]">
            <Receipt size={40} weight="light" className="mb-3 opacity-30" />
            <div className="text-[14px] font-semibold text-[#64748B] mb-0.5">{q ? "No invoices match your search" : "No invoices yet"}</div>
            <div className="text-[12px]">{q ? "Try a different search term" : "Create a sale to see it here"}</div>
          </div>
        ) : (
          <div className="divide-y divide-[#F1F5F9]">
            {filtered.map((sale) => {
              const uStatus = getUnifiedStatus(sale);
              const methodLabel = sale._is_online_invoice
                ? (sale._oi_status === "sent" ? "Sent" : sale._oi_status === "viewed" ? "Viewed" : "Online")
                : METHOD_LABELS[(sale.tyro_transaction_reference || (sale.payments || []).some((p) => !p.corrected_at && (p.tyro_meta || p.method === "tyro"))) ? "tyro" : (sale.payment_method || "cash")] || "";
              const delta = paymentDelta(sale);

              const statusBadge = {
                paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
                balance_due: "bg-amber-50 text-amber-700 border-amber-200",
                refunded: "bg-rose-50 text-rose-700 border-rose-200",
                part_refunded: "bg-orange-50 text-orange-700 border-orange-200",
                voided: "bg-[#F1F5F9] text-[#94A3B8] border-[#CBD5E1]",
              };

              return (
                <div
                  key={sale.id}
                  className="grid grid-cols-[110px_1.5fr_160px_110px_120px_60px] gap-3 px-5 py-3.5 items-center hover:bg-[#F8FAFC] transition-colors duration-150 cursor-pointer"
                  onClick={() => openDetail(sale)}
                  data-testid={`inv-row-${sale.sale_number}`}
                >
                  <div className="font-mono text-[12px] font-bold text-[#1E5FEB] flex items-center gap-1.5">
                    {sale._is_online_invoice && <Globe size={12} weight="bold" className="text-violet-500 shrink-0" />}
                    {sale.sale_number}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[#0F172A] truncate flex items-center gap-1.5">
                      <span className="truncate">{sale.customer_name || "Walk-in"}</span>
                      {sale._stripe && (
                        <span
                          className={`shrink-0 inline-flex items-center gap-1 h-[18px] px-1.5 rounded-md border text-[9px] font-bold uppercase tracking-wider ${
                            sale._stripe === "link" ? "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E5FEB]"
                              : sale._stripe === "paid" ? "bg-[#F5F3FF] border-[#DDD6FE] text-[#635BFF]"
                              : "bg-slate-50 border-slate-200 text-slate-400"
                          }`}
                          title={sale._stripe === "link" ? "Stripe link sent — waiting for payment" : sale._stripe === "paid" ? "Paid online with Stripe" : "Stripe link cancelled"}
                          data-testid={`stripe-badge-${sale.sale_number}`}
                        >
                          Stripe{sale._stripe === "link" ? " · link sent" : sale._stripe === "cancelled" ? " · cancelled" : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#94A3B8] truncate">
                      {sale.item_count || 0} item{(sale.item_count || 0) !== 1 ? "s" : ""}
                      {methodLabel ? ` · ${methodLabel}` : ""}
                      {sale.cashier_name ? ` · ${sale.cashier_name}` : ""}
                    </div>
                  </div>
                  <div className="text-[12px] text-[#64748B]">
                    {fmtInvoiceDate(sale.created_at)}
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${statusBadge[uStatus] || statusBadge.balance_due}`} data-testid={`status-${sale.sale_number}`}>
                      {STATUS_LABELS_MAP[uStatus] || uStatus}
                    </span>
                    {(uStatus === "refunded" || uStatus === "part_refunded") && (
                      <span className="text-[9.5px] font-semibold text-rose-700 tabular-nums" data-testid={`refunded-amt-${sale.sale_number}`}>
                        -${money(sale.refund_total)}
                      </span>
                    )}
                    {delta && delta.overpaid && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid={`overpaid-${sale.sale_number}`}>
                        <Warning size={9} weight="fill" /> +${money(delta.amount)}
                      </span>
                    )}
                    {delta && !delta.overpaid && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`underpaid-${sale.sale_number}`}>
                        <Clock size={9} weight="fill" /> -${money(delta.amount)}
                      </span>
                    )}
                  </div>
                  <div className="text-right font-mono text-[14px] font-extrabold text-[#0F172A] tabular-nums">
                    ${money(sale.total)}
                  </div>
                  <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        const pdfUrl = sale._is_online_invoice
                          ? `${process.env.REACT_APP_BACKEND_URL}/api/online-invoices/${sale.id}/pdf`
                          : `${process.env.REACT_APP_BACKEND_URL}/api/sales/${sale.id}/pdf`;
                        const token = localStorage.getItem("pos_token");
                        fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } })
                          .then((r) => r.blob())
                          .then((blob) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${sale.sale_number}.pdf`; a.click(); })
                          .catch(() => toast.error("PDF download failed"));
                      }}
                      className="w-8 h-8 rounded-lg border border-[#E2E8F0] flex items-center justify-center text-[#94A3B8] hover:text-[#0F172A] hover:border-[#0F172A] hover:bg-[#F8FAFC] transition-all duration-150"
                      title="Download PDF"
                      data-testid={`pdf-${sale.sale_number}`}
                    >
                      <Download size={13} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-[#F1F5F9] bg-[#FAFBFC] flex items-center justify-between">
            <span className="text-[11px] text-[#94A3B8] font-semibold tabular-nums">
              Showing {filtered.length} invoice{filtered.length !== 1 ? "s" : ""}
              {statusFilter !== "all" && ` (${STATUS_LABELS_MAP[statusFilter]})`}
            </span>
            <span className="text-[11px] text-[#94A3B8] font-mono tabular-nums">
              {dateRange.mode !== "all" && dateRange.from ? `${dateRange.from.toLocaleDateString(undefined, { month: "short", day: "numeric" })} → ${dateRange.to ? new Date(dateRange.to.getTime() - 1).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}` : ""}
            </span>
          </div>
        )}
      </section>

      {/* Unified Invoice Detail Modal */}
      {selectedInvoice && (
        <UnifiedInvoiceModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onUpdated={() => { setSelectedInvoice(null); loadSales(); }}
          onAddPayment={(inv) => { setSelectedInvoice(null); setAddPaymentInvoice(inv); }}
          user={user}
        />
      )}

      {/* Add Payment Modal */}
      {addPaymentInvoice && (
        <AddPaymentModal
          invoice={addPaymentInvoice}
          user={user}
          onClose={() => setAddPaymentInvoice(null)}
          onDone={() => { setAddPaymentInvoice(null); loadSales(); }}
        />
      )}
    </Layout>
  );
}
